/**
 * Shared TanStack Query option constants.
 *
 * Centralizes the polling intervals, stale times, and garbage-collection times
 * that were previously hard-coded across the data hooks. The `POLL_*` objects
 * are designed to be spread into `useQuery` options, e.g.
 * `useQuery(args, { ...POLL_SLOW, enabled, placeholderData })`. The `STALE_*`
 * and `GC_*` scalars are assigned to `staleTime` / `gcTime`.
 *
 * Values mirror the existing inline literals so adoption is behavior-preserving.
 *
 * @packageDocumentation
 */

/** Polling options for fast-refresh queries (10s interval, foreground only) */
export const POLL_FAST = { refetchInterval: 10_000, refetchIntervalInBackground: false } as const;

/** Polling options for normal-refresh queries (15s interval, foreground only) */
export const POLL_NORMAL = { refetchInterval: 15_000, refetchIntervalInBackground: false } as const;

/** Polling options for slow-refresh queries (30s interval, foreground only) */
export const POLL_SLOW = { refetchInterval: 30_000, refetchIntervalInBackground: false } as const;

/** Polling options for history/trend queries (60s interval, foreground only) */
export const POLL_HISTORY = { refetchInterval: 60_000, refetchIntervalInBackground: false } as const;

/** Short stale window: 2 minutes */
export const STALE_SHORT = 2 * 60 * 1000;

/** Medium stale window: 5 minutes */
export const STALE_MEDIUM = 5 * 60 * 1000;

/** Long stale window: 10 minutes */
export const STALE_LONG = 10 * 60 * 1000;

/** Medium garbage-collection window: 10 minutes */
export const GC_MEDIUM = 10 * 60 * 1000;
